import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@lynx/ui-components";
import { Spinner } from "react-bootstrap";

import TableComponent from "ui/ui-kit/react-table/TableComponent";
import { supportTableUtils } from "ui/ui-kit/react-table/supportTableUtils";
import WireManagementStateController from "./state-controller/WireManagementStateController";
import WireConfirmationModal from "./WireConfirmationModal";
import WireDetailsModal from "./WireDetailsModal";
import CreateCustomRequestModal from "../account-overview/modals/CreateCustomRequestModal";
import { ModalContext } from "ui/ui-kit/modal/ModalContext";
import useStatistic from "ui/hooks/useStatistic";
import { accountThunks } from "actions-thunks/account-thunks";
import { requestsThunks } from "actions-thunks/requests-thunks";
import constants from "lib/constants";

import "./wire-management.scss";

const { categoryData } = constants;

const WireManagementPage = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.utils);
  const { currentAccount, wiresList, wiresListTotal } = useSelector((state) => state.accounts);
  const [visibleItems, setVisibleItems] = useState(10);
  const [showWireConfirmationModal, setShowWireConfirmationModal] = useState(false);
  const { showModal, hideModal } = useContext(ModalContext);

  useStatistic(currentAccount?._id, "Open page Wire Management");

  useEffect(() => {
    if (currentAccount?._id) {
      dispatch(accountThunks.getWiresListTC(currentAccount._id, {
        page: 1,
        pageLen: 25,
        sort: "-createdAt",
      }));
    }
  }, [currentAccount, dispatch]);

  // Format data for table with icons in Type and Status columns
  const showRowOverlay = useCallback((row, isLastItem) => {
    const rowData = row.original;
    // Access the stored wire object from the table data
    const wire = rowData._wire;
    
    if (!wire || !wire._id) return null;

    const handleClick = (e) => {
      e.stopPropagation();
      showModal(<WireDetailsModal wire={wire} onClose={hideModal} />);
    };

    const isDesktop = window.innerWidth >= 640;
    const renderPart = (
      <td className="hover-content" onClick={handleClick}>
        <div className="three-dots-icon">
          <img 
            src={`${process.env.PUBLIC_URL}/icons/more-vertical.svg`}
            alt="more options"
            className="three-dots-icon-img"
          />
        </div>
      </td>
    );

    if (isDesktop) {
      return renderPart;
    }
    return <div className="relative-wrapper">{renderPart}</div>;
  }, [showModal, hideModal]);

  const tableData = useMemo(() => {
    if (!wiresList || !Array.isArray(wiresList)) {
      return [];
    }

    return wiresList.map((wire) => {
      const isDeposit = true; // TODO: remove this after backend is updated payout wire type
      
      const displayType = isDeposit ? "Deposit" : "Pay-out";
      
      const typeIconPath = isDeposit 
        ? `${process.env.PUBLIC_URL}/icons/wire-confirmation.svg`
        : `${process.env.PUBLIC_URL}/icons/payout.svg`;
      
      const backendStatus = wire.status || wire.state || "";
      let statusText = backendStatus.toUpperCase();
      let statusIconPath;
      let statusClass;
      
      // Normalize status values
      if (statusText.includes("PROGRESS") || statusText === "PENDING" || statusText === "PROCESSING") {
        statusText = "IN PROGRESS";
        statusIconPath = `${process.env.PUBLIC_URL}/icons/inprogress.svg`;
        statusClass = "status-in-progress";
      } else if (statusText.includes("CONFIRM") || statusText === "COMPLETED" || statusText === "RECEIVED" || statusText === "SENT") {
        statusText = "CONFIRMED";
        statusIconPath = `${process.env.PUBLIC_URL}/icons/done.svg`;
        statusClass = "status-confirmed";
      } else if (statusText.includes("DECLIN") || statusText === "REJECTED" || statusText === "FAILED") {
        statusText = "DECLINED";
        statusIconPath = `${process.env.PUBLIC_URL}/icons/decline.svg`;
        statusClass = "status-declined";
      } else if (statusText === "CANCELLED" || statusText === "CANCELED") {
        statusText = "CANCELLED";
        statusIconPath = `${process.env.PUBLIC_URL}/icons/decline.svg`;
        statusClass = "status-declined";
      } else {
        // Default to in progress if status is unknown
        statusText = "IN PROGRESS";
        statusIconPath = `${process.env.PUBLIC_URL}/icons/inprogress.svg`;
        statusClass = "status-in-progress";
      }
      
      // Format amount - use pending value
      const amount = wire.amount?.claimed || 0;
      const formattedAmount = typeof amount === 'number' 
        ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        : amount;
      
      // Use requestedOn as the creation date (when the wire was requested)
      const createdDate = wire.requestedOn || wire.createdAt || wire.created || wire.date || new Date().toISOString();
      
      return {
        Type: (
          <span className="type-cell-with-icon">
            <img 
              src={typeIconPath} 
              alt={displayType} 
              className={`type-icon ${isDeposit ? 'type-icon-deposit' : 'type-icon-payout'}`}
            />
            {displayType}
          </span>
        ),
        Amount: formattedAmount,
        Status: (
          <span className={`status-cell-with-icon ${statusClass}`}>
            <img 
              src={statusIconPath} 
              alt={statusText} 
              className="status-icon"
            />
            {statusText}
          </span>
        ),
        Created: createdDate,
        _wire: wire, // Store original wire object for details modal
      };
    });
  }, [wiresList]);

  const handleWireConfirmation = () => {
    setShowWireConfirmationModal(true);
  };

  const handleWireConfirmationSuccess = () => {
    // Refresh wires list after successful confirmation
    if (currentAccount?._id) {
      dispatch(accountThunks.getWiresListTC(currentAccount._id, {
        page: 1,
        pageLen: 25,
        sort: "-createdAt",
      }));
    }
  };

  const handleRequestPayout = () => {
    const categories = [
      { value: categoryData.CUSTOM, label: categoryData.CUSTOM },
    ];
    const activeCategory = categories[0];

    const handleCloseModal = () => {
      hideModal();
    };

    const handleSubmitModal = async ({ body, type, isCustomForm = false }) => {
      const { label, ...rest } = body;
      let attachmentsIDs = null;
      let requestBody = rest;

      if (isCustomForm) {
        attachmentsIDs = await dispatch(
          accountThunks.setAccountAttachmentsTC(currentAccount._id, rest)
        );

        if (attachmentsIDs.status === 200) {
          requestBody = {
            ...rest,
            ext: {
              ...rest.ext,
              attachments: attachmentsIDs.data.attachments.map((item) => item.id),
            },
          };
        }
      }

      dispatch(
        requestsThunks.createDataRequestTC(currentAccount._id, requestBody, type)
      );
      handleCloseModal();
    };

    const handleUpdateCategory = (selectedCategory) => {
      // For payout requests, we always use Custom category
      // This function is required by the modal but won't change anything
    };

    showModal(
      <CreateCustomRequestModal
        closeModal={handleCloseModal}
        updateCategory={handleUpdateCategory}
        categories={categories}
        activeCategory={activeCategory}
        submitModal={handleSubmitModal}
      />
    );
  };

  const handlePayoutSettings = () => {
    // TODO: Implement payout settings functionality
    console.log("Payout settings clicked");
  };

  return (
    <div className="wire-management-wrapper">
      {loading && <Spinner className="spinner" animation="grow" />}

      {/* <div className="wire-management-header">
        <div className="wire-management-title-section">
          <div className="automatic-payout-section">
            <span className="automatic-payout-text">
              Automatic payout <span className="enabled-text">enabled</span>
            </span>
          </div>
        </div>
        <div className="wire-management-header-actions">
          <Button
            buttonStyle="button-variant-fourth"
            buttonClassName="cabinet-secondary-btn"
            onClick={handlePayoutSettings}
            label={
              <span>
                <Icon name="settings" className="button-icon" />
                Payout settings
              </span>
            }
          />
        </div>
      </div> */}

      <div className="wire-management-content">
        <div className="wire-management-actions">
          <span className="c-grey-text"><strong>Operations</strong></span>
          <div className="wire-management-actions-buttons">
            <Button
              buttonStyle="button-variant-second"
              buttonClassName="cabinet-primary-btn"
              onClick={handleWireConfirmation}
              label={
                <span>
                  <img 
                    src={`${process.env.PUBLIC_URL}/icons/wire-confirmation.svg`} 
                    alt="wire confirmation" 
                    className="button-icon" 
                  />
                  Wire Confirmation
                </span>
              }
            />
            {/* <div className="button-separator"></div>
            <Button
              buttonStyle="button-variant-second"
              buttonClassName="cabinet-primary-btn"
              onClick={handleRequestPayout}
              label={
                <span>
                  <img 
                    src={`${process.env.PUBLIC_URL}/icons/payout.svg`} 
                    alt="payout" 
                    className="button-icon" 
                  />
                  Request Pay-Out
                </span>
              }
            /> */}
          </div>
        </div>

        <div className="wire-management-table">
          <div className="wire-management-table-body">
            <TableComponent
              data={tableData.slice(0, visibleItems)}
              dateColumns={["Created"]}
              formattingForDateColumns={supportTableUtils.tableTimeFormat}
              showSearch={false}
              itemsToUseSorting={["Type", "Amount", "Status", "Created"]}
              setCellClassName={(cell) => {
                if (cell.column.id === "Type") {
                  return "type-cell";
                }
                if (cell.column.id === "Status") {
                  return "status-cell";
                }
                return "";
              }}
              showRowOverlay={showRowOverlay}
            />
          </div>
          <WireManagementStateController
            setVisibleItems={setVisibleItems}
            visibleItems={visibleItems}
            items={tableData}
            totalItems={wiresListTotal || tableData.length}
          />
        </div>
      </div>

      {showWireConfirmationModal && (
        <WireConfirmationModal
          onClose={() => setShowWireConfirmationModal(false)}
          onSuccess={handleWireConfirmationSuccess}
        />
      )}
    </div>
  );
};

export default WireManagementPage;
